<template>
  <Modal
    width="small"
    compact
    @close="$emit('close')"
    @submit="emitSubmit"
    :texts="{ headerPrimary: 'scan_or_enter_device_code' }"
    :isConfirmDisabled="!canConfirm"
  >
    <section class="scanner-container">
      <div class="camera-section" v-if="!cameraUnavailable">
        <div class="video-wrapper" @click="onPreviewTap">
          <video ref="videoEl" class="scanner-video" playsinline muted />
          <div class="scan-overlay" aria-hidden="true">
            <div class="scan-frame">
              <span class="corner corner-tl" />
              <span class="corner corner-tr" />
              <span class="corner corner-bl" />
              <span class="corner corner-br" />
            </div>
          </div>
        </div>
        <p class="scan-hint">{{ $t('scan_qr_in_frame') }}</p>
      </div>
      <p v-if="cameraError" class="camera-message">{{ cameraError }}</p>

      <div class="input-container">
        <div>{{ $t('deviceCode') }}</div>
        <input
          type="text"
          class="modal-input"
          v-model="deviceCodeInput"
          @input="onDeviceCodeInput"
          autofocus
        />
      </div>

      <DeviceCodeValidationStatus
        :state="validationState"
        :duplicateMessage="deviceCodeDuplicateMessage"
        :errorMessage="deviceCodeErrorMessage"
      />
    </section>
  </Modal>
</template>

<script>
import Modal from '../../common/components/Modal';
import DeviceCodeValidationStatus from './DeviceCodeValidationStatus';
import {
	deviceCodeValidationMixin,
	isDeviceCodeSubmitAllowed,
	parseDeviceCodeFromInput,
} from '../utils/deviceCodeUtils';
import { createQrCodeReader, refocusCamera, startQrVideoScan } from '../utils/deviceQrScannerUtils';

export default {
	mixins: [deviceCodeValidationMixin],
	props: {
		currentDeviceCode: { type: String, required: true },
		currentSensorId: { type: [Number, String], default: null },
		isAdmin: { type: Boolean, default: false },
	},
	computed: {
		isAdminUser() {
			return this.isAdmin;
		},
		canConfirm() {
			return isDeviceCodeSubmitAllowed(this.validationState, !!this.deviceCodeInput.trim());
		},
	},
	data() {
		return {
			deviceCodeInput: '',
			cameraError: '',
			cameraUnavailable: false,
			reader: null,
			controls: null,
			qrScanLocked: false,
			lockedScanCode: '',
		};
	},
	async mounted() {
		await this.startCamera();
	},
	beforeDestroy() {
		this.stopCamera();
	},
	methods: {
		handleQrScanResult(result) {
			if (!result || this.qrScanLocked) return;
			const parsed = parseDeviceCodeFromInput(result.getText());
			if (!parsed) return;
			this.qrScanLocked = true;
			this.lockedScanCode = parsed;
			this.deviceCodeInput = parsed;
			this.runValidation();
		},
		onDeviceCodeInput() {
			const parsed = parseDeviceCodeFromInput(this.deviceCodeInput);
			if (this.qrScanLocked && parsed !== this.lockedScanCode) {
				this.qrScanLocked = false;
				this.lockedScanCode = '';
			}
			this.scheduleDeviceCodeValidation(() => this.runValidation());
		},
		async onPreviewTap() {
			await refocusCamera(this.$refs.videoEl);
		},
		async startCamera() {
			try {
				const { reader } = await createQrCodeReader();
				this.reader = reader;
				this.controls = await startQrVideoScan(reader, this.$refs.videoEl, (result) => {
					this.handleQrScanResult(result);
				});
			} catch (err) {
				this.cameraUnavailable = true;
				this.cameraError = this.$t('camera_not_available');
			}
		},
		stopCamera() {
			if (this.controls?.stop) this.controls.stop();
			this.controls = null;
			this.reader = null;
			const video = this.$refs.videoEl;
			if (video?.srcObject) {
				video.srcObject.getTracks().forEach((track) => track.stop());
				video.srcObject = null;
			}
		},
		runValidation() {
			this.validateAndApplyDeviceCode(
				this.deviceCodeInput,
				this.currentSensorId,
				this.currentDeviceCode,
				() => this.deviceCodeInput,
				(value) => { this.deviceCodeInput = value; },
			);
		},
		emitSubmit() {
			if (!this.canConfirm) return;
			this.$emit('submit', { newDeviceCode: parseDeviceCodeFromInput(this.deviceCodeInput) });
		},
	},
	components: { Modal, DeviceCodeValidationStatus },
};
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.scanner-container {
	display: flex;
	flex-direction: column;
	gap: 16px;
	width: 100%;
	min-width: 0;
}

.camera-section {
	display: flex;
	flex-direction: column;
	gap: 10px;
	width: 100%;
}

.video-wrapper {
	position: relative;
	width: 100%;
	aspect-ratio: 4 / 3;
	max-height: min(38vh, 260px);
	overflow: hidden;
	border-radius: 8px;
	background: #000;
	cursor: pointer;
}

.scanner-video {
	width: 100%;
	height: 100%;
	max-height: min(38vh, 260px);
	object-fit: contain;
	display: block;
	background: #000;
}

.scan-overlay {
	position: absolute;
	inset: 0;
	z-index: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	pointer-events: none;
}

.scan-frame {
	position: relative;
	width: min(62%, 200px);
	aspect-ratio: 1;
	border: 2px solid rgba(255, 255, 255, 0.9);
	border-radius: 10px;
	box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45);
}

.corner {
	position: absolute;
	width: 18px;
	height: 18px;
	border-color: #389ceb;
	border-style: solid;
}

.corner-tl {
	top: -2px;
	left: -2px;
	border-width: 3px 0 0 3px;
	border-top-left-radius: 6px;
}

.corner-tr {
	top: -2px;
	right: -2px;
	border-width: 3px 3px 0 0;
	border-top-right-radius: 6px;
}

.corner-bl {
	bottom: -2px;
	left: -2px;
	border-width: 0 0 3px 3px;
	border-bottom-left-radius: 6px;
}

.corner-br {
	bottom: -2px;
	right: -2px;
	border-width: 0 3px 3px 0;
	border-bottom-right-radius: 6px;
}

.scan-hint {
	margin: 0;
	font-size: 13px;
	line-height: 1.45;
	color: #666;
	text-align: center;
}

.camera-message {
	margin: 0;
	padding: 10px 12px;
	font-size: 14px;
	line-height: 1.45;
	color: #666;
	background: #f8f9fa;
	border-radius: 8px;
}

.input-container {
	width: 100%;
	min-width: 0;

	input {
		@include modal-input;
		padding: 0 12px;
		width: 100%;
		box-sizing: border-box;
		min-height: 44px;
		font-size: 16px;
	}

	div {
		@include modal-input-label;
		margin-bottom: 6px;
	}
}

@media (max-width: 820px) {
	.scanner-container {
		gap: 14px;
	}

	.video-wrapper,
	.scanner-video {
		max-height: min(42vh, 300px);
	}
}

@media (max-width: 480px) {
	.scanner-container {
		gap: 12px;
	}

	.video-wrapper,
	.scanner-video {
		max-height: min(36vh, 220px);
		border-radius: 6px;
	}

	.scan-hint,
	.camera-message {
		font-size: 12px;
	}

	.input-container input {
		min-height: 48px;
	}
}
</style>
